import {Menu} from '@mantine/core';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo} from 'react';
import type {
  FormRelatedTaskSimpleResponse} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  PermissionOperationEnum,
  TaskAssignmentStateEnum,
  TaskDefinitionStateEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {TaskAssignmentStateDataOptions} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {useGetFormRelatedTasksListV2Query} from 'src/redux/queries/form';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {
  GetModuleEnumFromTaskType,
  TaskAssignmentPillConfig,
} from 'src/app/tasks/taskHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface RelatedTaskAssignmentsListProps {
  formId?: number;
}
export const RelatedTaskAssignmentsList: React.FC<
  RelatedTaskAssignmentsListProps
> = ({formId}) => {
  const navigate = useAppNavigate();

  const columns = useMemo<MRT_ColumnDef<FormRelatedTaskSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
      },
      {
        accessorKey: 'taskDefinitionId',
        header: 'Task Definition ID',
      },
      {
        accessorKey: 'taskAssignmentId',
        header: 'Task Assignment ID',
      },
      {
        accessorKey: 'formId',
        header: 'Form ID',
      },
      {
        accessorKey: 'subject',
        header: 'Subject',
      },
      {
        accessorKey: 'assignedTo',
        header: 'Assigned To',
      },
      {
        accessorKey: 'dueAt',
        header: 'Due In',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
      {
        accessorKey: 'state',
        header: 'State',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: TaskAssignmentStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill
            {...TaskAssignmentPillConfig(
              cell.getValue<TaskAssignmentStateEnum>(),
              cell.row.getValue('taskType'),
              TaskDefinitionStateEnum.Published,
              undefined
            )[0]}
          />
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'form-related-tasks',
    columns: columns,
    defaultColumnFilters: [
      {
        id: 'state',
        value: [
          TaskAssignmentStateEnum.Assigned.toString(),
          TaskAssignmentStateEnum.Viewed.toString(),
        ],
      },
    ],
    defaultColumnVisibility: {
      id: false,
      taskDefinitionId: false,
      taskAssignmentId: false,
      formId: false,
    },
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'dueAt', desc: true}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetFormRelatedTasksListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
      formId: formId,
    });
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActionMenuItems: ({row}) => (
        <>
          <PermissionGuard
            module={GetModuleEnumFromTaskType(row.original.taskType)}
            operation={PermissionOperationEnum.View}>
            <Menu.Item
              onClick={() => {
                navigate(
                  PathUtility.TaskView(
                    row.original.taskType,
                    row.original.taskDefinitionId,
                    'content'
                  )
                );
              }}>
              View
            </Menu.Item>
          </PermissionGuard>
        </>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Actions}>
      <DataHeader
        titleText="Assignments"
        isFilterActive={false}
        titleSize="md"
      />
      {table}
    </ContentWrapper>
  );
};
